import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarDismiss } from '@angular/material/snack-bar';

import { concatMap, defer, Observable, Subject, take } from 'rxjs';

import { TranslocoService } from '@jsverse/transloco';

import { API, BomExportStatusPath } from '@cdba/shared/constants/api';

import { BomExportStatus } from '../model/feature/bom-export';
import { Interaction } from '../model/interaction.model';
import { InteractionType } from '../model/interaction-type.enum';

@Injectable({ providedIn: 'root' })
export class UserInteractionService {
  private readonly snackbar = inject(MatSnackBar);
  private readonly translocoService = inject(TranslocoService);
  private readonly http = inject(HttpClient);

  private readonly interactionQueue$ = new Subject<InteractionType>();

  init(): void {
    this.interactionQueue$
      .pipe(
        concatMap((interactionType) =>
          defer(() => this.openSnackBar(interactionType))
        )
      )
      .subscribe();
  }

  /**
   * Puts an Interaction on the Queue. The Interaction will be shown as MatSnackBar with the message and
   * action provided by the interactionType
   *
   * @param {InteractionType} interactionType - The type of interaction to handle
   */
  interact(interactionType: InteractionType): void {
    this.interactionQueue$.next(interactionType);
  }

  private openSnackBar(
    interactionType: InteractionType
  ): Observable<MatSnackBarDismiss> {
    const interaction = this.determineInteractionByType(interactionType);

    const message = this.translocoService.translate(`${interaction.message}`);

    const actionMessage = this.translocoService.translate(
      `${interaction.action.message}`
    );

    const snackBarRef = this.snackbar.open(message, actionMessage);

    if (interaction.action.url && interaction.action.url !== '') {
      snackBarRef
        .onAction()
        .pipe(take(1))
        .subscribe(() => {
          window.open(interaction.action.url, '_blank').focus();
        });
    }

    return snackBarRef.afterDismissed();
  }

  /**
   * Loads the initial BOM (Bill of Materials) export status.
   *
   * @returns An Observable that emits the current BOM export status.
   */
  loadInitialBomExportStatus(): Observable<BomExportStatus> {
    return this.http.get<BomExportStatus>(`${API.v1}/${BomExportStatusPath}`);
  }

  // Required due to the unserializable action exception from NgRx
  private determineInteractionByType(
    interactionType: InteractionType
  ): Interaction {
    let interaction: Interaction;
    switch (interactionType) {
      case InteractionType.HTTP_GENERAL_ERROR: {
        interaction = Interaction.HTTP_GENERAL_ERROR;
        break;
      }
      case InteractionType.HTTP_GENERAL_VALIDATION_ERROR: {
        interaction = Interaction.HTTP_GENERAL_VALIDATION_ERROR;
        break;
      }
      case InteractionType.REQUEST_BOM_EXPORT_SUCCESS: {
        interaction = Interaction.REQUEST_BOM_EXPORT_SUCCESS;
        break;
      }
      case InteractionType.REQUEST_BOM_EXPORT_VALIDATION_ERROR: {
        interaction = Interaction.REQUEST_BOM_EXPORT_VALIDATION_ERROR;
        break;
      }
      case InteractionType.REQUEST_BOM_EXPORT_FAILURE: {
        interaction = Interaction.REQUEST_BOM_EXPORT_FAILURE;
        break;
      }
      case InteractionType.TRACK_BOM_EXPORT_PROGRESS_COMPLETED: {
        interaction = Interaction.TRACK_BOM_EXPORT_PROGRESS_COMPLETED;
        break;
      }
      case InteractionType.TRACK_BOM_EXPORT_PROGRESS_FAILURE: {
        interaction = Interaction.TRACK_BOM_EXPORT_PROGRESS_FAILURE;
        break;
      }
      case InteractionType.GET_BOM_EXPORT_STATUS_FAILURE: {
        interaction = Interaction.GET_BOM_EXPORT_STATUS_FAILURE;
        break;
      }
      default: {
        throw new Error(`Unhandled InteractionType: ${interactionType}`);
      }
    }

    return interaction;
  }
}
